import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { diffWords, isUnchanged } from "@/utils/word-diff";
import type { ContentType } from "@/types/ab-tests";
import { VariantBDiffText } from "./VariantBDiffText";

interface VariantBFieldEditorProps {
  field: ContentType;
  fieldValue: string;
  aSource: string;
  charLimit: number;
  label: string;
  isEditing: boolean;
  fieldSource?: "ai" | "ai-edited" | "manual";
  focusValue: string;
  /** WEB-003: one `viewChanges` state, owned by `VariantBPane`, drives every field. */
  viewChanges: boolean;
  /** WEB-003: only `selectedFields[0]`'s row renders the `View changes` switch. */
  isFirstField: boolean;
  onViewChangesChange: (value: boolean) => void;
  onFocus: (val: string) => void;
  onBlur: (field: ContentType, val: string) => void;
  onChange: (val: string) => void;
  onClick: () => void;
  onFieldSourceChange?: (field: string, source: "ai" | "ai-edited" | "manual") => void;
}

export function VariantBFieldEditor({
  field,
  fieldValue,
  charLimit,
  label,
  aSource,
  isEditing,
  fieldSource,
  focusValue,
  viewChanges,
  isFirstField,
  onViewChangesChange,
  onFocus,
  onBlur,
  onChange,
  onClick,
  onFieldSourceChange,
}: VariantBFieldEditorProps) {
  // `decisions/the-variant-b-diff-is-computed-in-the-browser.md`: computed on
  // every render, not cached — Variant B changes on every keystroke.
  const segments = useMemo(() => diffWords(aSource, fieldValue), [aSource, fieldValue]);
  const unchanged = isUnchanged(segments);
  const showNoChangesYet = viewChanges && !isEditing && unchanged;
  const showDiff = viewChanges && !unchanged;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 min-h-[20px]">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-medium text-primary uppercase tracking-wide">{label}</p>
          {fieldSource === "ai" && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              <AISparkles className="h-2.5 w-2.5" />
              AI
            </span>
          )}
          {fieldSource === "ai-edited" && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
              <AISparkles className="h-2.5 w-2.5" />
              AI · edited
            </span>
          )}
          {showNoChangesYet && <span className="text-xs text-muted-foreground">No changes yet</span>}
        </div>
        {isFirstField && !isEditing && (
          <div className="flex items-center gap-2">
            <Label htmlFor="variant-b-view-changes" className="text-xs text-muted-foreground">
              View changes
            </Label>
            <Switch
              id="variant-b-view-changes"
              aria-label="Show what changed between Variant A and Variant B"
              checked={viewChanges}
              onCheckedChange={onViewChangesChange}
            />
          </div>
        )}
      </div>
      <div
        className="rounded-lg border border-primary bg-card min-h-[80px] p-3 cursor-text transition-colors"
        onClick={onClick}
      >
        {isEditing ? (
          <textarea
            autoFocus
            value={fieldValue}
            onFocus={(e) => onFocus(e.target.value)}
            onBlur={(e) => {
              if (e.target.value !== focusValue) onBlur(field, e.target.value);
            }}
            onChange={(e) => {
              onChange(e.target.value);
              if (fieldSource === "ai" && onFieldSourceChange) {
                onFieldSourceChange(field, "ai-edited");
              }
            }}
            placeholder={`Enter variant ${label.toLowerCase()}...`}
            className="w-full h-full min-h-[80px] resize-none bg-transparent text-xs leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none"
            maxLength={charLimit}
          />
        ) : (
          <div key={fieldValue} className="animate-fade-in">
            {showDiff ? (
              <VariantBDiffText segments={segments} />
            ) : (
              <p className="text-base leading-relaxed text-foreground whitespace-pre-wrap">
                {fieldValue || "—"}
              </p>
            )}
          </div>
        )}
      </div>
      <p className={cn("text-[10px] text-right", fieldValue.length > charLimit ? "text-destructive font-medium" : "text-muted-foreground")}>
        {fieldValue.length} / {charLimit}
      </p>
    </div>
  );
}
